import { Component, Output, EventEmitter, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GraphService } from '../../services/administration/graph.service';
import { UserDto } from '../../interfaces/graph/user.interface';
import { debounceTime, distinctUntilChanged, switchMap, of } from 'rxjs';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-user-search',
    templateUrl: './user-search.component.html',
    styleUrls: ['./user-search.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UserSearchComponent),
            multi: true
        }
    ],
    standalone: false
})
export class UserSearchComponent implements ControlValueAccessor {
  @Input() placeholder: string = 'Start typing to search for users...';
  @Input() nameFieldId: string = '';
  @Input() emailFieldId: string = '';
  @Input() isRequired: boolean = false;
  @Output() userSelected = new EventEmitter<UserDto | null>();

  searchQuery: string = '';
  searchResults: UserDto[] = [];
  isSearching: boolean = false;
  showDropdown: boolean = false;
  selectedUser: UserDto | null = null;

  private searchSubject = new Subject<string>();
  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(private graphService: GraphService) {
    this.setupSearch();
  }

  private setupSearch(): void {
    this.searchSubject.pipe(
      debounceTime(300), // Wait 300ms after user stops typing
      distinctUntilChanged(),
      switchMap(query => {
        if (query.length >= 3) {
          this.isSearching = true;
          return this.graphService.searchForUserInEntra(query);
        } else {
          this.isSearching = false;
          return of([]);
        }
      })
    ).subscribe({
      next: (results) => {
        this.searchResults = results;
        this.isSearching = false;
        this.showDropdown = results.length > 0;
      },
      error: (error) => {
        console.error('Error searching users:', error);
        this.isSearching = false;
        this.searchResults = [];
        this.showDropdown = false;
      }
    });
  }

  onInputChange(value: string): void {
    this.searchQuery = value;
    this.searchSubject.next(value);
    
    // If user clears the input, clear the selection
    if (!value) {
      this.clearSelection();
    }
  }

  onInputFocus(): void {
    if (this.searchResults.length > 0) {
      this.showDropdown = true;
    }
  }

  onInputBlur(): void {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => {
      this.showDropdown = false;
    }, 200);
  }

  selectUser(user: UserDto): void {
    this.selectedUser = user;
    this.searchQuery = `${user.firstName} ${user.lastName}`.trim();
    this.showDropdown = false;
    this.userSelected.emit(user);
    this.onChange(this.searchQuery);
    this.onTouched();
  }

  clearSelection(): void {
    this.selectedUser = null;
    this.searchQuery = '';
    this.searchResults = [];
    this.showDropdown = false;
    this.userSelected.emit(null);
    this.onChange('');
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (value !== undefined) {
      this.searchQuery = value || '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handle disabled state if needed
  }

  getDisplayName(user: UserDto): string {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown User';
  }
}
